import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { forumAPI } from "@/lib/api";
import PageTitle from "@/components/PageTitle";
import ForumSpotlightRail from "@/components/ForumSpotlightRail";
import MemberBadge from "@/components/MemberBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, MessageSquare, Heart, Eye, MessagesSquare, Monitor, Gamepad2 } from "lucide-react";

export const FORUM_CATEGORIES = [
  { id: "general", label: "General" },
  { id: "setup_questions", label: "Setup Questions" },
  { id: "setup_tips", label: "Setup Tips" },
  { id: "car_setups", label: "Car Setups" },
  { id: "games_sims", label: "Games/Sims" },
  { id: "reviews", label: "Reviews" },
];

// Keep these in sync with mobile (mobile/src/constants/theme.ts)
export const EQUIPMENT_TAGS = [
  { id: "any", label: "Any Equipment" },
  { id: "wheel_base", label: "Wheel Base" },
  { id: "steering_wheel", label: "Steering Wheel" },
  { id: "pedals", label: "Pedals" },
  { id: "shifter", label: "Shifter" },
  { id: "handbrake", label: "Handbrake" },
  { id: "motion_system", label: "Motion System" },
  { id: "belt_tensioner", label: "Belt Tensioner" },
  { id: "cockpit", label: "Cockpit/Rig" },
  { id: "monitors", label: "Monitors" },
  { id: "vr", label: "VR Headset" },
  { id: "other", label: "Other" },
];

export const GAME_TAGS = [
  { id: "any", label: "Any Game/Sim" },
  { id: "iracing", label: "iRacing" },
  { id: "ac", label: "Assetto Corsa" },
  { id: "acc", label: "ACC" },
  { id: "ac_evo", label: "AC Evo" },
  { id: "lmu", label: "Le Mans Ultimate" },
  { id: "raceroom", label: "RaceRoom" },
  { id: "ams", label: "AMS" },
  { id: "ams2", label: "AMS 2" },
  { id: "rfactor2", label: "rFactor 2" },
  { id: "forza", label: "Forza" },
  { id: "gt", label: "Gran Turismo" },
  { id: "f1", label: "F1 Series" },
  { id: "other", label: "Other" },
];

export default function Forum() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [equipmentTag, setEquipmentTag] = useState("any");
  const [gameTag, setGameTag] = useState("any");

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 30 };
    if (search) params.search = search;
    if (category !== "all") params.category = category;
    if (equipmentTag !== "any") params.equipment_tag = equipmentTag;
    if (gameTag !== "any") params.game_tag = gameTag;
    forumAPI
      .getPosts(params)
      .then((res) => setPosts(res.data?.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [search, category, equipmentTag, gameTag]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="space-y-6">
      <PageTitle
        title="Forum"
        subtitle="Discuss setups, hardware & racing"
        action={
          <Button onClick={() => navigate("/forum/create")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="forum-create-button">
            <Plus className="mr-2 h-4 w-4" /> New post
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6 min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search discussions..." className="pl-9" data-testid="forum-search-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant={category === "all" ? "default" : "outline"} size="sm" onClick={() => setCategory("all")}>All</Button>
              {FORUM_CATEGORIES.map((c) => (
                <Button key={c.id} variant={category === c.id ? "default" : "outline"} size="sm" onClick={() => setCategory(c.id)}>{c.label}</Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2">
              <Gamepad2 className="h-4 w-4 shrink-0 text-primary" />
              <Select value={equipmentTag} onValueChange={setEquipmentTag}>
                <SelectTrigger className="w-full" data-testid="forum-filter-product"><SelectValue placeholder="Any Equipment" /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_TAGS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Monitor className="h-4 w-4 shrink-0 text-primary" />
              <Select value={gameTag} onValueChange={setGameTag}>
                <SelectTrigger className="w-full" data-testid="forum-filter-game"><SelectValue placeholder="Any Game/Sim" /></SelectTrigger>
                <SelectContent>
                  {GAME_TAGS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(equipmentTag !== "any" || gameTag !== "any") && (
              <Button variant="ghost" size="sm" onClick={() => { setEquipmentTag("any"); setGameTag("any"); }} data-testid="forum-filter-clear">Clear</Button>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
          ) : posts.length ? (
            <div className="divide-y divide-border/60">
              {posts.map((p) => {
                const isGame = p.category === "games_sims";
                const productName = p.equipment_tag && p.equipment_tag !== "any" ? p.equipment_tag : null;
                const simName = p.game_tag && p.game_tag !== "any" ? p.game_tag : null;
                return (
                  <Link key={p.id} to={`/forum/${p.id}`} data-testid={`forum-post-${p.id}`} className="group block">
                    <div className="relative -mx-2 rounded-lg border-b border-transparent px-2 py-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:bg-card/60 hover:shadow-[0_6px_20px_-10px_rgba(0,0,0,0.45)]">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-md border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground/80">
                          {(p.category || "general").replace("_", " ")}
                        </span>
                      </div>
                      {(productName || simName) && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {productName && (
                            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/80 px-2.5 py-1 text-xs text-foreground/85">
                              <Gamepad2 className="h-3.5 w-3.5 text-primary" />
                              <span className="font-semibold text-muted-foreground">Product:</span>
                              <span className="font-medium">{productName}</span>
                            </span>
                          )}
                          {simName && (
                            <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/80 px-2.5 py-1 text-xs text-foreground/85">
                              <Monitor className="h-3.5 w-3.5 text-primary" />
                              <span className="font-semibold text-muted-foreground">{isGame ? "Sim:" : "Game:"}</span>
                              <span className="font-medium">{simName}</span>
                            </span>
                          )}
                        </div>
                      )}
                      <h3 className="mt-3 text-xl font-semibold leading-tight sm:text-2xl">{p.title}</h3>
                      <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{p.content}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                        <span>by {p.author_username}</span>
                        <MemberBadge memberCategory={p.author_member_category} isFounding={p.author_is_founding} testId={`forum-member-badge-${p.id}`} />
                        <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {p.reply_count}</span>
                        <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {p.likes?.length || 0}</span>
                        <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {p.views}</span>
                        {p.tags && p.tags.length > 0 && (
                          <span className="truncate">
                            <span className="font-semibold text-foreground/80">Tags:</span> {p.tags.join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
              <MessagesSquare className="mb-3 h-12 w-12 text-muted-foreground" />
              <p className="font-medium">No discussions yet</p>
              <Button className="mt-3 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/forum/create")}>Start the first post</Button>
            </div>
          )}
        </div>

        <ForumSpotlightRail />
      </div>
    </div>
  );
}
